import React, { useEffect, useState } from 'react'
import { Link } from 'gatsby'
//MUI
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActionArea from '@mui/material/CardActionArea'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import useMediaQuery from '@mui/material/useMediaQuery'
import { CardMedia } from '@mui/material'

import RichText, { IRichText } from '../RichText'
import useSanitizedSlug from '../../hooks/useSanitizedSlug'
import Image from '../Image'
import HelpImage from '../Image/Variants/HelpImage'
import Form from '../Form'
import CodeModule from '../CodeModule'
import CallToAction from '../CallToAction'
import ChipComponent from '../Chip'
import HelpPageList from '../List/Variants/HelpPageList'
import { ContentfulToComponent, ContentfulToComponentProps } from '../RichText/types'
import { CardProps } from './Card.type'

const CardComponent = ({
  targetUrl,
  showArrowIcon,
  body,
  icon,
  targetEntry,
  imageAlignment,
  imageSize = 'Small',
  topAccent,
  cardStyles,
}: CardProps) => {
  const propsMap: Partial<ContentfulToComponentProps> = {
    HelpUniverseComponentImage: <HelpImageProps,>(props: HelpImageProps) => ({
      ...(props as unknown as HelpImageProps),
    }),
    HelpUniverseModuleDynamicComponents: <CodeModuleComponentProps,>(props: CodeModuleComponentProps) => ({
      ...(props as unknown as CodeModuleComponentProps),
    }),
    HelpUniverseModuleForm: <FormProps,>(props: FormProps) => ({
      ...(props as unknown as FormProps),
    }),
    HelpUniverseComponentCta: <CallToActionType,>(props: CallToActionType) => ({
      ...(props as unknown as CallToActionType),
    }),
    HelpUniverseComponentChip: <ChipProps,>(props: ChipProps) => ({
      ...(props as unknown as ChipProps),
    }),
    HelpUniverseComponentList: <HelpPageListComponentProps,>(props: HelpPageListComponentProps) => ({
      ...(props as unknown as HelpPageListComponentProps),
    }),
    HelpUniverseComponentCard: <CardProps,>(props: CardProps) => ({
      ...(props as unknown as CardProps),
    }),
  } as const

  const cardComponentMap: Partial<ContentfulToComponent> = {
    'HelpUniverseComponentImage': HelpImage,
    'HelpUniverseModuleForm': Form,
    'HelpUniverseModuleDynamicComponents': CodeModule,
    'HelpUniverseComponentCta': CallToAction,
    'HelpUniverseComponentChip': ChipComponent,
    'HelpUniverseComponentList': HelpPageList,
    'HelpUniverseComponentCard': CardComponent,
  } as const
  const { palette } = useTheme()
  const sanitizedSlug = useSanitizedSlug(`/kundeservice/${targetEntry?.backlink?.slug}/${targetEntry?.slug}`) // Generate sanitized slug for each article entry
  const linkType = targetEntry ? 'entry' : targetUrl ? 'url' : null
  const url = targetUrl?.startsWith('https://tdc.dk/') ? targetUrl.replace('https://tdc.dk', '') : targetUrl // prevent local and staging linking to tdc
  const target = linkType === 'entry' ? sanitizedSlug : linkType === 'url' ? url : null
  const isLargeCard = imageSize === 'Large' || imageSize === 'Large with padding'
  const [imageAlign, setImageAlign] = useState<string>(imageAlignment ?? 'Top')
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  useEffect(() => {
    if ((imageAlignment === 'Left' || imageAlignment === 'Right') && isMobile) {
      setImageAlign('Top')
    } else {
      setImageAlign(imageAlignment ?? 'Top')
    }
  }, [isMobile])

  // Base styles
  const baseStyles = {
    card: {
      color: cardStyles?.color ?? '',
      border: cardStyles?.borderColor ? `1px solid ${cardStyles?.borderColor}` : '',
      borderTop: topAccent ? `6px solid ${topAccent}` : '',
      backgroundColor: cardStyles?.backgroundColor ?? palette.common.white,
      height: '100%',
      textDecoration: 'none',
      '& .MuiCardActionArea-root': {
        height: '100%',
      },
      '& .MuiCardContent-root': {
        height: '100%',
      },
      '& .Mui-focusVisible': {
        background: 'none',
        transition: 'none',
        transform: 'none',
      },
      'span .MuiCardActionArea-focusHighlight': {
        background: 'none',
        transition: 'none',
        transform: 'none',
      },
      '.MuiCardActionArea-focusHighlight': {
        background: 'none',
        transition: 'none',
        transform: 'none',
      },
      ...(linkType
        ? {
            '&:hover': {
              backgroundColor: palette.grey[200],
            },
          }
        : {}),
    },
    richText: {
      '& p': {
        margin: 0,
      },
    },
    secondary: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1,
      marginLeft: 'auto',
    },
  }

  const imgSizes = {
    Small: {
      width: '70px',
      height: '70px',
      minWidth: '70px',
    },
    Medium: {
      width: '150px',
      height: '150px',
      minWidth: '150px',
    },
    Large: {
      width: '100%',
      height: 'auto',
    },
    'Large with padding': {
      width: '100%',
      height: 'auto',
    },
  }

  const imgSize = imgSizes[imageSize] ?? imgSizes['Small']

  const styles = {
    content: {
      display: 'flex',
      padding: topAccent ? '24px' : !icon ? '10px !important' : '24px',
    },
    wrapper: {
      display: 'flex',
      flexDirection:
        imageAlign === 'Top' || imageAlign === 'Right' ? 'column' : imageAlign === 'Bottom' ? 'column-reverse' : '',
      gap: '24px',
      alignItems: imageAlign === 'Left' || !imageAlign ? 'center' : '',
      width: '100%',
    },
    media: {
      ...imgSize,
      flexShrink: 0,
    },
  }

  const carouselSyles = {
    content: {
      display: 'flex',
      flexDirection:
        imageAlign === 'Top'
          ? 'column'
          : imageAlign === 'Bottom'
            ? 'column-reverse'
            : imageAlignment === 'Right'
              ? 'row-reverse'
              : 'row',
      alignItems: 'center',
      gap: imageSize === 'Large with padding' ? '24px' : '',
      padding: imageSize === 'Large' ? '0px !important' : '24px',
    },
    media: {
      flexShrink: 0,
      width: '100%',
      height: imageAlign === 'Left' || imageAlign === 'Right' ? '100%' : 'auto',
      maxWidth: imageAlign === 'Left' || imageAlign === 'Right' ? '30%' : '',
    },
    secondary: {
      display: 'flex',
      padding:
        imageSize === 'Large' && (imageAlign === 'Top' || imageAlign === 'Bottom')
          ? '10px'
          : imageSize === 'Large'
            ? '24px'
            : '',
      width: '100%',
      gap: imageAlign === 'Top' || imageAlign === 'Bottom' ? '12px' : '',
      flexDirection: imageAlign === 'Top' || imageAlign === 'Bottom' ? 'row' : 'column',
      justifyContent: imageAlign === 'Top' || imageAlign === 'Bottom' ? 'space-between' : 'center',
      alignItems: imageAlign === 'Top' || imageAlign === 'Bottom' ? 'center' : 'flex-start',
      zIndex: 1,
    },
    richText: {
      '.MuiTypography-h6': {
        color: palette.primary.light,
        margin: 0,
      },
    },
  }

  const cardContent = (
    <CardContent sx={styles.content}>
      <Box sx={styles.wrapper}>
        {icon && imageAlign !== 'Right' && <CardMedia component={Image} imageStyles={styles.media} {...icon} />}
        <RichText
          sx={baseStyles.richText}
          {...(body as IRichText)}
          componentMap={cardComponentMap as ContentfulToComponent}
          propsMap={propsMap as ContentfulToComponentProps}
        />
        {showArrowIcon && imageAlign === 'Right' && <ArrowForwardIcon />}
      </Box>
      {((showArrowIcon && imageAlign !== 'Right') || (icon && imageAlign === 'Right')) && (
        <Box sx={baseStyles.secondary}>
          {showArrowIcon && imageAlign !== 'Right' && <ArrowForwardIcon />}
          {icon && imageAlign === 'Right' && <CardMedia component={Image} imageStyles={styles.media} {...icon} />}
        </Box>
      )}
    </CardContent>
  )
  const carouselCard = (
    <CardContent sx={carouselSyles.content}>
      <CardMedia component={Image} imageStyles={carouselSyles.media} {...icon} />
      <Box sx={carouselSyles.secondary}>
        <RichText
          sx={{ ...baseStyles.richText, ...carouselSyles.richText }}
          {...(body as IRichText)}
          componentMap={cardComponentMap as ContentfulToComponent}
          propsMap={propsMap as ContentfulToComponentProps}
        />
        {showArrowIcon && <ArrowForwardIcon />}
      </Box>
    </CardContent>
  )

  return (
    <Card variant="outlined" sx={baseStyles.card}>
      {target ? (
        <CardActionArea component={Link} to={target}>
          {isLargeCard ? carouselCard : cardContent}
        </CardActionArea>
      ) : isLargeCard ? (
        carouselCard
      ) : (
        cardContent
      )}
    </Card>
  )
}

export default CardComponent
